import { useSyncExternalStore } from 'react'
import { getCurrentUser } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'

// Amplify の認証状態を保持・broadcast する軽量ストア。Me キャッシュとは無関係。
// 状態は startAuthListener が Hub と起動判定から更新し、UI は useAuthStatus、
// API クライアント(client.ts の onRequest)は「認証確定まで待つ」ために listen で購読する。
export type AuthStatus = 'configuring' | 'unauthenticated' | 'authenticated'

export const auth_listener = (() => {
  const listeners: ((state: AuthStatus, cancel: () => void) => void)[] = []
  let state: AuthStatus = 'configuring'
  return {
    listen (cb: (state: AuthStatus, cancel: () => void) => void) {
      listeners.push(cb)
      return () => {
        const index = listeners.indexOf(cb)
        if (index >= 0) listeners.splice(index, 1)
      }
    },
    update (s: AuthStatus) {
      state = s
      for (const cb of [...listeners]) cb(s, () => {
        const i = listeners.indexOf(cb)
        if (i >= 0) listeners.splice(i, 1)
      })
    },
    get state () { return state }
  }
})()

// TokenOrchestrator.isAuthenticationError と同じ一覧。SDK がトークンを消すのはこのときだけ
const TOKEN_CLEARING_ERRORS = [
  'NotAuthorizedException', 'TokenRevokedException', 'UserNotFoundException',
  'PasswordResetRequiredException', 'UserNotConfirmedException', 'RefreshTokenReuseException',
]
const isTokenClearing = (e: unknown) => {
  const name = (e as {name?: unknown} | undefined)?.name
  return typeof name === 'string' && TOKEN_CLEARING_ERRORS.some(n => name.startsWith(n))
}

let started = false
/** Amplify.configure() の直後に一度だけ呼ぶ。冪等。 */
export const startAuthListener = () => {
  if (started) return
  started = true
  // 起動判定中のトークン更新が、トークンを残したまま失敗したか（通信エラー等）
  let refresh_failed_with_tokens = false
  Hub.listen('auth', ({payload}) => {
    switch (payload.event) {
      case 'signedIn': auth_listener.update('authenticated'); break
      case 'signedOut': auth_listener.update('unauthenticated'); break
      // 一時的な障害ではトークンが残るので認証済みのまま（API 側で失敗・再試行）
      case 'tokenRefresh_failure':
        if (isTokenClearing(payload.data?.error)) auth_listener.update('unauthenticated')
        else refresh_failed_with_tokens = true
        break
    }
  })
  // 起動判定は、先に Hub イベントで状態が決まっていれば反映しない
  const settle = (s: AuthStatus) => { if (auth_listener.state === 'configuring') auth_listener.update(s) }
  // getCurrentUser の reject は、トークンなし・設定不備・トークン更新の失敗のいずれか。
  // 更新の失敗は reject の前に Hub へ同期的に dispatch されるので、トークンが残る場合だけ認証済みにする
  getCurrentUser().then(
    () => settle('authenticated'),
    () => settle(refresh_failed_with_tokens ? 'authenticated' : 'unauthenticated'),
  )
}

export const useAuthStatus = () =>
  useSyncExternalStore(cb => auth_listener.listen(cb), () => auth_listener.state)
